import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";

const ADDONS_SPACING = 0; // u Ciebie 0

function normalizeWallKey(wall) {
  const w = String(wall || "").toLowerCase();
  if (w.includes("left")) return "left";
  if (w.includes("right")) return "right";
  if (w.includes("back")) return "back";
  if (w.includes("front")) return "front";
  return "other";
}

// ✅ U CIEBIE: front/back -> X, left/right -> Z
function getWallNormalAxis(wall) {
  const w = String(wall || "").toLowerCase();
  const isSideWall = w.includes("left") || w.includes("right");
  return isSideWall ? "z" : "x";
}

function padAxis(box, axis, pad) {
  if (axis === "x") {
    box.min.x -= pad;
    box.max.x += pad;
  } else if (axis === "y") {
    box.min.y -= pad;
    box.max.y += pad;
  } else if (axis === "z") {
    box.min.z -= pad;
    box.max.z += pad;
  }
}

function getWorldBoxAddon(item) {
  if (!item?.closedBoundingBox?.box || !item?.wall) return null;

  const src = item.closedBoundingBox.box;
  const box = new THREE.Box3(src.min.clone(), src.max.clone());

  const wallNormalAxis = getWallNormalAxis(item.wall);

  padAxis(box, "y", ADDONS_SPACING);
  padAxis(box, wallNormalAxis, ADDONS_SPACING);

  return box;
}

function segmentAabbInterval(a, b, box) {
  const d = new THREE.Vector3().subVectors(b, a);

  let tmin = 0;
  let tmax = 1;

  const axes = ["x", "y", "z"];
  for (const ax of axes) {
    const origin = a[ax];
    const dir = d[ax];
    const min = box.min[ax];
    const max = box.max[ax];

    if (Math.abs(dir) < 1e-12) {
      if (origin < min || origin > max) return null;
      continue;
    }

    let t1 = (min - origin) / dir;
    let t2 = (max - origin) / dir;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
    }

    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);

    if (tmin > tmax) return null;
  }

  const s = Math.max(0, tmin);
  const e = Math.min(1, tmax);
  if (e <= 0 || s >= 1 || e - s <= 1e-6) return null;

  return [s, e];
}

function mergeIntervals(intervals, eps = 1e-5) {
  if (!intervals.length) return [];
  const arr = intervals
    .map(([a, b]) => [Math.min(a, b), Math.max(a, b)])
    .sort((i1, i2) => i1[0] - i2[0]);

  const merged = [arr[0]];
  for (let i = 1; i < arr.length; i++) {
    const cur = arr[i];
    const last = merged[merged.length - 1];
    if (cur[0] <= last[1] + eps) {
      last[1] = Math.max(last[1], cur[1]);
    } else {
      merged.push(cur);
    }
  }
  return merged;
}

function subtractIntervals01(cuts, minKeepT = 0.02) {
  if (!cuts.length) return [[0, 1]];
  const merged = mergeIntervals(cuts);

  const out = [];
  let cursor = 0;

  for (const [s, e] of merged) {
    const a = Math.max(0, s);
    const b = Math.min(1, e);

    if (a > cursor + minKeepT) out.push([cursor, a]);
    cursor = Math.max(cursor, b);
  }

  if (1 > cursor + minKeepT) out.push([cursor, 1]);
  return out;
}

function lerpV3(a, b, t) {
  return new THREE.Vector3(
    a.x + (b.x - a.x) * t,
    a.y + (b.y - a.y) * t,
    a.z + (b.z - a.z) * t
  );
}

function useOptionalTexture(url) {
  const [tex, setTex] = useState(null);

  useEffect(() => {
    if (!url) {
      setTex(null);
      return;
    }

    let alive = true;
    const loader = new THREE.TextureLoader();

    loader.load(
      url,
      (t) => {
        if (!alive) {
          t.dispose?.();
          return;
        }

        try {
          t.colorSpace = THREE.SRGBColorSpace;
        } catch (e) {}

        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.repeat.set(1, 1);
        t.needsUpdate = true;

        setTex(t);
      },
      undefined,
      () => {
        if (alive) setTex(null);
      }
    );

    return () => {
      alive = false;
    };
  }, [url]);

  useEffect(() => () => tex?.dispose?.(), [tex]);

  return tex;
}

/**
 * ✅ FIX: nie używamy boxGeometry args z długością (R3F czasem nie przebudowuje geometrii)
 * tylko stała geometria 1×1×1 i długość idzie w scale.
 */
function Beam({ a, b, thickness = 0.05, overlap = 0.02, material }) {
  const { position, quaternion, length } = useMemo(() => {
    const A = a?.isVector3 ? a : new THREE.Vector3(a?.x || 0, a?.y || 0, a?.z || 0);
    const B = b?.isVector3 ? b : new THREE.Vector3(b?.x || 0, b?.y || 0, b?.z || 0);

    const dir = new THREE.Vector3().subVectors(B, A);
    const baseLen = dir.length();
    const safeLen = Math.max(baseLen, 1e-6);

    const mid = new THREE.Vector3().addVectors(A, B).multiplyScalar(0.5);

    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());

    const extendedLen = safeLen + 2 * overlap;

    return { position: mid, quaternion: q, length: extendedLen };
  }, [
    a?.x, a?.y, a?.z,
    b?.x, b?.y, b?.z,
    overlap,
  ]);

  return (
    <mesh
      position={position}
      quaternion={quaternion}
      material={material}
      castShadow
      receiveShadow
      scale={[thickness, length, thickness]}
    >
      <boxGeometry args={[1, 1, 1]} />
    </mesh>
  );
}

// ✅ pionowe przetłoczenie -> PLUS, poziome -> VERTICAL
function pickReinforcementModeFromPressing(pressingStr) {
  const p = (pressingStr || "").toLowerCase();
  if (p.includes("horizontal")) return "VERTICAL";
  if (p.includes("vertical")) return "PLUS";
  return "VERTICAL";
}

function toV3(p) {
  if (!p) return new THREE.Vector3();
  return p.isVector3 ? p : new THREE.Vector3(p.x, p.y, p.z);
}

function computeWallBounds(toolingData) {
  const corners = toolingData?.corners || [];
  if (corners.length) {
    const pts = [];
    for (const c of corners) {
      if (c?.a) pts.push(toV3(c.a));
      if (c?.b) pts.push(toV3(c.b));
    }
    if (pts.length >= 2) {
      const box = new THREE.Box3().setFromPoints(pts);
      return { minX: box.min.x, maxX: box.max.x, minZ: box.min.z, maxZ: box.max.z };
    }
  }

  const edges = toolingData?.roofEdges || [];
  const pts = [];
  for (const e of edges) {
    if (e?.a) pts.push(toV3(e.a));
    if (e?.b) pts.push(toV3(e.b));
  }
  if (pts.length >= 2) {
    const box = new THREE.Box3().setFromPoints(pts);
    return { minX: box.min.x, maxX: box.max.x, minZ: box.min.z, maxZ: box.max.z };
  }

  return { minX: -5, maxX: 5, minZ: -3, maxZ: 3 };
}

function computeRoofMaxY(toolingData) {
  const edges = toolingData?.roofEdges || [];
  const pts = [];
  for (const e of edges) {
    if (e?.a) pts.push(toV3(e.a));
    if (e?.b) pts.push(toV3(e.b));
  }
  if (pts.length >= 2) {
    const box = new THREE.Box3().setFromPoints(pts);
    return box.max.y;
  }
  return 3;
}

function buildCeilingSampler(
  toolingData,
  roofMaxY,
  floorY,
  { fallbackCeilPadding = 0.02, triEpsXZ = 1e-3, edgeMaxDist = 5.0 } = {}
) {
  const trisRaw = Array.isArray(toolingData?.roofTriangles) ? toolingData.roofTriangles : null;

  if (trisRaw && trisRaw.length) {
    const triData = trisRaw.map((t) => {
      const a = toV3(t.a),
        b = toV3(t.b),
        c = toV3(t.c);
      const minX = Math.min(a.x, b.x, c.x);
      const maxX = Math.max(a.x, b.x, c.x);
      const minZ = Math.min(a.z, b.z, c.z);
      const maxZ = Math.max(a.z, b.z, c.z);
      return { a, b, c, minX, maxX, minZ, maxZ };
    });

    const ray = new THREE.Ray();
    const origin = new THREE.Vector3();
    const dir = new THREE.Vector3(0, 1, 0);
    const hit = new THREE.Vector3();

    return (x, z) => {
      origin.set(x, floorY - 1.0, z);
      ray.set(origin, dir);

      let bestY = Infinity;

      for (let i = 0; i < triData.length; i++) {
        const t = triData[i];
        if (x < t.minX - triEpsXZ || x > t.maxX + triEpsXZ) continue;
        if (z < t.minZ - triEpsXZ || z > t.maxZ + triEpsXZ) continue;

        const p = ray.intersectTriangle(t.a, t.b, t.c, false, hit);
        if (!p) continue;
        if (p.y >= origin.y && p.y < bestY) bestY = p.y;
      }

      if (bestY !== Infinity) return bestY;
      return roofMaxY - fallbackCeilPadding;
    };
  }

  const edges = toolingData?.roofEdges || [];
  const segs = edges.filter((e) => e?.a && e?.b).map((e) => ({ a: toV3(e.a), b: toV3(e.b) }));

  return (x, z) => {
    if (!segs.length) return roofMaxY - fallbackCeilPadding;

    let bestD2 = Infinity;
    let bestY = roofMaxY - fallbackCeilPadding;

    for (let i = 0; i < segs.length; i++) {
      const A = segs[i].a;
      const B = segs[i].b;

      const vx = B.x - A.x;
      const vz = B.z - A.z;
      const wx = x - A.x;
      const wz = z - A.z;

      const vv = vx * vx + vz * vz;
      if (vv < 1e-12) continue;

      let t = (wx * vx + wz * vz) / vv;
      t = Math.max(0, Math.min(1, t));

      const cx = A.x + t * vx;
      const cz = A.z + t * vz;

      const dx = x - cx;
      const dz = z - cz;
      const d2 = dx * dx + dz * dz;

      if (d2 < bestD2) {
        bestD2 = d2;
        bestY = A.y + t * (B.y - A.y);
      }
    }

    if (bestD2 <= edgeMaxDist * edgeMaxDist) return bestY;
    return roofMaxY - fallbackCeilPadding;
  };
}

export default function Reinforcements({
                                         toolingData,
                                         textures,

                                         doorsData,
                                         windowsData,

                                         garageWidth,  // cm
                                         garageLength, // cm

                                         reinforcementColor,

                                         thickness = 0.03,
                                         overlap = 0.015,

                                         insideInset = 0,
                                         cornerMargin = 0.02,

                                         // (zostawiamy, ale w VERTICAL liczymy ilości, nie spacing)
                                         spacing = 1.2,

                                         floorY = 0,
                                         ceilingInset = 0.01,
                                         fallbackCeilPadding = 0.02,

                                         minWallSafety = 0.02,

                                         cutoutExtraPad = 0.03,
                                         minKeepLength = 0.12,

                                         plusStepY = 1.0,
                                       }) {
  if (!toolingData) return null;

  const wallBounds = useMemo(() => computeWallBounds(toolingData), [toolingData]);
  const roofMaxY = useMemo(() => computeRoofMaxY(toolingData), [toolingData]);

  const pressingStr = textures?.walls_color?.pressing || "";
  const mode = useMemo(() => pickReinforcementModeFromPressing(pressingStr), [pressingStr]);

  const ceilYAt = useMemo(() => {
    return buildCeilingSampler(toolingData, roofMaxY, floorY, { fallbackCeilPadding });
  }, [toolingData, roofMaxY, floorY, fallbackCeilPadding]);

  // ===== materiał =====
  const maybeUrl =
    typeof textures?.plastic_default?.texture === "string"
      ? textures.plastic_default.texture
      : typeof textures?.metal_default?.texture === "string"
        ? textures.metal_default.texture
        : null;

  const mapUrl = useOptionalTexture(maybeUrl);

  const mapKtx2 = textures?.plastic_default?.textureDiff || textures?.plastic_default?.texture || null;

  const normalKtx2 =
    textures?.plastic_default?.textureNormalness ||
    textures?.plastic_default?.textureNormal ||
    textures?.plastic_default?.textureNormalMap ||
    null;

  const finalMap = mapUrl || mapKtx2;

  useEffect(() => {
    if (!finalMap) return;
    finalMap.wrapS = THREE.RepeatWrapping;
    finalMap.wrapT = THREE.RepeatWrapping;
    finalMap.repeat?.set?.(1, 1);
    finalMap.offset?.set?.(0, 0);
    finalMap.needsUpdate = true;
  }, [finalMap]);

  useEffect(() => {
    if (!normalKtx2) return;
    normalKtx2.wrapS = THREE.RepeatWrapping;
    normalKtx2.wrapT = THREE.RepeatWrapping;
    normalKtx2.repeat?.set?.(1, 1);
    normalKtx2.offset?.set?.(0, 0);
    normalKtx2.needsUpdate = true;
  }, [normalKtx2]);

  const reinforcementMaterial = useMemo(() => {
    const wallColor = textures?.walls_color;
    const hexa = reinforcementColor?.hex || reinforcementColor?.hexa || wallColor?.hex || "#d9d9d9";

    const isWood = reinforcementColor?.type === "wooden";
    const metalness = reinforcementColor?.metalnessValue ?? (isWood ? 0 : 0);
    const roughness = reinforcementColor?.roughnessValue ?? (isWood ? 0.9 : 0.65);

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(hexa),
      metalness,
      roughness,
      side: THREE.FrontSide,
    });

    if (finalMap) mat.map = finalMap;
    if (normalKtx2) mat.normalMap = normalKtx2;

    mat.needsUpdate = true;
    return mat;
  }, [
    textures?.walls_color,
    reinforcementColor?.hex,
    reinforcementColor?.hexa,
    reinforcementColor?.type,
    reinforcementColor?.metalnessValue,
    reinforcementColor?.roughnessValue,
    finalMap,
    normalKtx2,
  ]);

  useEffect(() => () => reinforcementMaterial?.dispose?.(), [reinforcementMaterial]);

  // ===== CUT BOXES (drzwi + okna) =====
  const cutBoxesByWall = useMemo(() => {
    const boxes = { left: [], right: [], back: [] };

    const flattenedDoors = Object.entries(doorsData || {}).flatMap(([wall, doors]) =>
      (doors || []).map((d) => ({ ...d, wall }))
    );

    const flattenedWindows = Object.entries(windowsData || {}).flatMap(([wall, windowTypes]) =>
      Object.entries(windowTypes || {})
        .filter(([key]) => key !== "count")
        .flatMap(([type, windows]) => (windows || []).map((w) => ({ ...w, wall, type })))
    );

    const addons = [...flattenedDoors, ...flattenedWindows];

    for (const item of addons) {
      const wk = normalizeWallKey(item.wall);
      if (wk !== "left" && wk !== "right" && wk !== "back") continue;

      const b = getWorldBoxAddon(item);
      if (!b) continue;

      const wallNormalAxis = getWallNormalAxis(item.wall);

      const reachInside = insideInset + thickness + cutoutExtraPad;
      padAxis(b, wallNormalAxis, reachInside);

      const scalarPad = thickness * 0.5 + cutoutExtraPad;
      const bb = b.clone();
      bb.expandByScalar(scalarPad);

      boxes[wk].push(bb);
    }

    return boxes;
  }, [doorsData, windowsData, thickness, cutoutExtraPad, insideInset]);

  // ===== SEGMENTS + CUT =====
  const segmentsFinal = useMemo(() => {
    const segs = [];

    const { minX, maxX, minZ, maxZ } = wallBounds;

    const safeInset = Math.max(insideInset, minWallSafety) + thickness * 0.5;

    // ✅ left/right = Z, back = X
    const leftZ = minZ + safeInset;
    const rightZ = maxZ - safeInset;
    const backX = maxX - safeInset;

    const spanX0 = minX + cornerMargin;
    const spanX1 = maxX - cornerMargin;

    const spanZ0 = minZ + cornerMargin;
    const spanZ1 = maxZ - cornerMargin;

    // ✅ stabilnie z propsów (cm -> m)
    const eps = 1e-6;
    const garageLengthM = Math.max(0, (Number(garageLength) || 0) / 100);
    const garageWidthM = Math.max(0, (Number(garageWidth) || 0) / 100);

    // floor tak, żeby 6.50 -> 6 (a nie 7)
    const lengthNominal = Math.floor(garageLengthM + eps);
    const widthNominal = Math.floor(garageWidthM + eps);

    const topCut = ceilingInset + thickness * 0.5;
    const clampTop = (y) => Math.max(floorY + 0.05, y - topCut);

    const pushSegment = (wallKeyOrKeys, a, b) => {
      const keys = Array.isArray(wallKeyOrKeys) ? wallKeyOrKeys : [wallKeyOrKeys];

      const boxes = [];
      for (const k of keys) {
        const arr = cutBoxesByWall[k];
        if (arr?.length) boxes.push(...arr);
      }

      const cuts = [];
      for (let i = 0; i < boxes.length; i++) {
        const interval = segmentAabbInterval(a, b, boxes[i]);
        if (interval) cuts.push(interval);
      }

      const len = a.distanceTo(b);
      const minKeepT = len > 1e-6 ? minKeepLength / len : 1;

      const keeps = subtractIntervals01(cuts, Math.max(0.01, minKeepT));

      for (const [t0, t1] of keeps) {
        const aa = lerpV3(a, b, t0);
        const bb = lerpV3(a, b, t1);
        if (aa.distanceTo(bb) >= minKeepLength) {
          segs.push({ a: aa, b: bb });
        }
      }
    };

    const pushVertical = (wallKeyOrKeys, x, z) => {
      const yTop = clampTop(ceilYAt(x, z));
      pushSegment(wallKeyOrKeys, new THREE.Vector3(x, floorY, z), new THREE.Vector3(x, yTop, z));
    };

    const minCeilOnSideSegment = (x0, x1, zConst, samples = 13) => {
      let best = Infinity;
      for (let i = 0; i < samples; i++) {
        const t = samples === 1 ? 0 : i / (samples - 1);
        const x = x0 + (x1 - x0) * t;
        best = Math.min(best, clampTop(ceilYAt(x, zConst)));
      }
      return best === Infinity ? clampTop(roofMaxY) : best;
    };

    const minCeilOnBackSegment = (xConst, z0, z1, samples = 17) => {
      let best = Infinity;
      for (let i = 0; i < samples; i++) {
        const t = samples === 1 ? 0 : i / (samples - 1);
        const z = z0 + (z1 - z0) * t;
        best = Math.min(best, clampTop(ceilYAt(xConst, z)));
      }
      return best === Infinity ? clampTop(roofMaxY) : best;
    };

    // ===== PLUS helpers =====
    const addCornerPostsPlus = () => {
      pushVertical("left", spanX0, leftZ);
      pushVertical("right", spanX0, rightZ);

      pushVertical(["left", "back"], backX, leftZ);
      pushVertical(["right", "back"], backX, rightZ);
    };

    const pushHorizontalsSide = (wallKey, zConst) => {
      const cap = minCeilOnSideSegment(spanX0, spanX1, zConst, 15);
      const yEnd = cap - 0.06;

      for (let y = floorY + plusStepY; y <= yEnd + 1e-6; y += plusStepY) {
        pushSegment(wallKey, new THREE.Vector3(spanX0, y, zConst), new THREE.Vector3(spanX1, y, zConst));
      }
    };

    const pushHorizontalsBack = () => {
      const cap = minCeilOnBackSegment(backX, spanZ0, spanZ1, 19);
      const yEnd = cap - 0.06;

      for (let y = floorY + plusStepY; y <= yEnd + 1e-6; y += plusStepY) {
        pushSegment("back", new THREE.Vector3(backX, y, spanZ0), new THREE.Vector3(backX, y, spanZ1));
      }
    };

    // ===== MODE: VERTICAL (przetłoczenie poziome) =====
    if (mode === "VERTICAL") {
      // BOKI:
      // 7m -> 6 wewnętrznych, 12m -> 11 wewnętrznych
      const internalSide = Math.max(4, Math.max(0, lengthNominal) - 1);

      // TYŁ:
      // <= 3m => 3 belki, 4m=>4, 5m=>5... (czyli max(3, floor(width)))
      const internalBack = Math.max(2, Math.max(0, widthNominal) - 1);
      const countBackTotal = internalBack + 2; // +2 rogi

      const usableX = Math.max(0.001, spanX1 - spanX0);
      const usableZ = Math.max(0.001, spanZ1 - spanZ0);

      pushVertical("left", spanX0, leftZ);
      pushVertical("left", spanX1, leftZ);
      pushVertical("right", spanX0, rightZ);
      pushVertical("right", spanX1, rightZ);

      // left/right: belki na równych odstępach (wliczamy końce)
      for (let i = 1; i <= internalSide; i++) {
        const t = i / (internalSide + 1); // nie dotyka 0 ani 1
        const x = spanX0 + usableX * t;

        pushVertical("left", x, leftZ);
        pushVertical("right", x, rightZ);
      }

      // back: belki na równych odstępach (wliczamy końce)
      for (let i = 0; i < countBackTotal; i++) {
        const t = countBackTotal <= 1 ? 0 : i / (countBackTotal - 1);
        const z = spanZ0 + usableZ * t; // równo dzieli całą ścianę (wlicza rogi)
        pushVertical("back", backX, z);
      }

      return segs;
    }

    // ===== MODE: PLUS (przetłoczenie pionowe) =====
    addCornerPostsPlus();

    // side horizontals + vertical dividers (z poprzedniej logiki)
    const plusCountSide = Math.max(1, lengthNominal - 4);
    const plusCountBack = widthNominal >= 6 ? 1 + Math.floor((widthNominal - 6) / 3) : 0;

    pushHorizontalsSide("left", leftZ);
    pushHorizontalsSide("right", rightZ);
    pushHorizontalsBack();

    const usableSide = Math.max(0.001, spanX1 - spanX0);
    for (let i = 1; i <= plusCountSide; i++) {
      const t = i / (plusCountSide + 1);
      const x = spanX0 + usableSide * t;
      pushVertical("left", x, leftZ);
      pushVertical("right", x, rightZ);
    }

    if (plusCountBack > 0) {
      const usableBack = Math.max(0.001, spanZ1 - spanZ0);
      for (let i = 1; i <= plusCountBack; i++) {
        const t = i / (plusCountBack + 1);
        const z = spanZ0 + usableBack * t;
        pushVertical("back", backX, z);
      }
    }

    return segs;
  }, [
    wallBounds,
    roofMaxY,
    mode,
    insideInset,
    minWallSafety,
    thickness,
    cornerMargin,
    floorY,
    ceilingInset,
    ceilYAt,
    cutBoxesByWall,
    minKeepLength,
    plusStepY,
    garageWidth,
    garageLength,
  ]);

  return (
    <group>
      {segmentsFinal.map((seg) => (
        <Beam
          key={`reinforcement-${seg.a.x.toFixed(3)}-${seg.a.y.toFixed(3)}-${seg.a.z.toFixed(3)}-${seg.b.x.toFixed(3)}-${seg.b.y.toFixed(3)}-${seg.b.z.toFixed(3)}`}
          a={seg.a}
          b={seg.b}
          thickness={thickness}
          overlap={overlap}
          material={reinforcementMaterial}
        />
      ))}
    </group>
  );
}
